import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { RefreshCw } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import apiService from '../../services/api';
import { API_CONFIG } from '../../config/api';

const colorPalette = ['#8B5CF6', '#6366F1', '#06B6D4', '#22C55E', '#F59E0B', '#EF4444'];

const InverterBreakdownChart = ({ startDate, endDate, assetIds = [] }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [seriesKeys, setSeriesKeys] = useState([]);
  const [rows, setRows] = useState([]);
  const [maxVal, setMaxVal] = useState(0);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const res = await apiService.getForEnergyLoss({
        start_date: `${startDate} 00:00:00`,
        end_date: `${endDate} 23:55:00`,
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: assetIds.length ? assetIds : [API_CONFIG.PLANT_ID]
      });
      const arr = Array.isArray(res.data) ? res.data : (res.data?.body || []);
      if (!Array.isArray(arr) || arr.length === 0) {
         setSeriesKeys([]);
         setRows([]);
         return;
       }
      const breakdownMap = arr[0]?.readings?.[0]?.EYA?.inverter_breakdown_loss_list_kwh || {};
      const rowsArr = Object.entries(breakdownMap).map(([inv, val]) => ({
        inverter: inv,
        value: Number(parseFloat(val).toFixed(2))
      }));

      const max = Math.ceil(Math.max(...rowsArr.map(r => r.value), 0));
      setMaxVal(max);
      setRows(rowsArr);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const bars = useMemo(() => seriesKeys.map((key, idx) => (
    null
  )), [seriesKeys]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Inverter Breakdown (kWh)</CardTitle>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-80 w-full" />
        ) : rows.length === 0 ? (
          <div className="text-center text-gray-500">No Inverter Breakdown data</div>
        ) : (
          <div className="w-full h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ top: 10, right: 20, bottom: 60, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" angle={-45} textAnchor="end" height={70} tick={{ fontSize: 11 }} />
                <XAxis dataKey="inverter" angle={-90} textAnchor="end" interval={0} height={70} tick={{ fontSize: 11 }} />
                <YAxis domain={[0, maxVal]} label={{ value: 'Energy (kWh)', angle: -90, position: 'insideLeft' }} />
                <Tooltip formatter={(value) => [`${Number(value).toFixed(2)} kWh`, 'Breakdown']} />
                <Bar dataKey="value" name="Breakdown (kWh)" fill="#8B5CF6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InverterBreakdownChart;


